import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Mic, Square, Pause, Play, Volume2, VolumeX, RefreshCw, SkipForward,
  Keyboard, PhoneOff, Loader2, Check, ListChecks, Lightbulb, HelpCircle,
  Wand2, ArrowLeft, ShieldCheck, Radio, AlertTriangle, Clock, ShoppingCart, Download,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import { FullPageLoader } from "@/components/Common";
import { api } from "@/lib/api";
import { downloadServerFile } from "@/lib/exporter";
import { toast } from "sonner";

const pickMime = () => {
  const opts = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  for (const m of opts) if (window.MediaRecorder?.isTypeSupported?.(m)) return m;
  return "";
};

const MAX_MINUTES = 15;
const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function BlueprintVoiceInterview() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const blueprintId = params.get("bp");

  const [phase, setPhase] = useState("intro"); // intro | active | review
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false); // processing an answer
  const [session, setSession] = useState(null);
  const [question, setQuestion] = useState(null); // {text, audio}
  const [lastTranscript, setLastTranscript] = useState("");
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [textValue, setTextValue] = useState("");
  const [review, setReview] = useState(null);
  const [approved, setApproved] = useState({});
  const [edits, setEdits] = useState({});
  const [applying, setApplying] = useState(false);
  const [remaining, setRemaining] = useState(null); // seconds left in the 15-min cap

  const audioRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!blueprintId) navigate("/build", { replace: true });
    return () => {
      try { audioRef.current?.pause(); } catch { /* noop */ }
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    };
  }, [blueprintId, navigate]);

  // Live 15-minute countdown, driven by the session start time (server-authoritative cap).
  useEffect(() => {
    if (phase !== "active" || !session?.started_at) { setRemaining(null); return; }
    const start = new Date(session.started_at).getTime();
    const deadline = start + MAX_MINUTES * 60 * 1000;
    const tick = () => setRemaining(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phase, session?.started_at]);

  const play = (url) => {
    if (!url || muted) return;
    try {
      audioRef.current?.pause();
      const a = new Audio(url);
      audioRef.current = a;
      setSpeaking(true);
      a.onended = () => setSpeaking(false);
      a.play().catch(() => setSpeaking(false));
    } catch { setSpeaking(false); }
  };

  const applyState = (d) => {
    setSession(d);
    if (d.question) { setQuestion(d.question); play(d.question.audio); }
  };

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/voice/session/start", { blueprint_id: blueprintId });
      setPhase("active");
      applyState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the interview.");
    } finally { setLoading(false); }
  };

  const sendAnswer = async (formOrBody) => {
    setBusy(true);
    let fd = formOrBody;
    if (!(formOrBody instanceof FormData)) {
      fd = new FormData();
      Object.entries(formOrBody).forEach(([k, v]) => fd.append(k, v));
    }
    try {
      const { data } = await api.post(`/voice/session/${session.session_id}/respond`, fd);
      setLastTranscript(data.transcript || "");
      setSession(data);
      setTextValue("");
      if (data.done) {
        await endInterview(true);
      } else if (data.question) {
        setQuestion(data.question);
        play(data.question.audio);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not process your answer. Please try again.");
    } finally { setBusy(false); }
  };

  // ---- recording ----
  const startRecording = async () => {
    if (timeUp) { toast.error("Time's up. Please end and review."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        streamRef.current?.getTracks?.().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 800) { toast.error("That was too short. Please hold the mic and speak your answer."); return; }
        const fd = new FormData();
        fd.append("file", blob, "answer.webm");
        await sendAnswer(fd);
      };
      audioRef.current?.pause();
      setSpeaking(false);
      rec.start();
      setRecording(true); setPaused(false);
    } catch {
      toast.error("Microphone access is needed. Allow it, or use Switch to text.");
    }
  };
  const stopRecording = () => { try { recRef.current?.stop(); } catch { /* noop */ } setRecording(false); setPaused(false); };
  const pauseRecording = () => { try { recRef.current?.pause(); setPaused(true); } catch { /* noop */ } };
  const resumeRecording = () => { try { recRef.current?.resume(); setPaused(false); } catch { /* noop */ } };

  const repeat = () => { if (question?.audio) { setMuted(false); play(question.audio); } };
  const toggleMute = () => {
    setMuted((m) => {
      const nm = !m;
      if (nm) { try { audioRef.current?.pause(); } catch { /* noop */ } setSpeaking(false); }
      return nm;
    });
  };

  const skip = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/voice/session/${session.session_id}/skip`);
      if (data.done) await endInterview(true);
      else applyState(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not skip."); }
    finally { setBusy(false); }
  };

  const endInterview = async (auto = false) => {
    if (recording) stopRecording();
    setBusy(true);
    try {
      const { data } = await api.post(`/voice/session/${session.session_id}/end`);
      setReview(data);
      const preset = {};
      (data.proposals || []).forEach((p) => { preset[p.id] = true; });
      setApproved(preset);
      setPhase("review");
      if (!auto) toast.success("Interview wrapped up. Review the proposed updates below.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not end the interview."); }
    finally { setBusy(false); }
  };

  const applyUpdates = async () => {
    setApplying(true);
    try {
      const ids = Object.keys(approved).filter((id) => approved[id]);
      const { data } = await api.post(`/voice/session/${review.session_id}/apply`, {
        approved_proposal_ids: ids, edits,
      });
      toast.success(data.applied > 0 ? `Applied ${data.applied} update${data.applied === 1 ? "" : "s"} to your blueprint.` : "No updates applied. Nothing changed.");
      navigate("/build");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update the blueprint."); }
    finally { setApplying(false); }
  };

  const continueInterview = async () => {
    setReview(null); setPhase("intro");
    await start();
  };

  const downloadRecap = async () => {
    try {
      await downloadServerFile(`/voice/session/${review.session_id}/recap.pdf`, "blueprint-interview-recap.pdf");
    } catch { toast.error("Could not download the recap. Please try again."); }
  };

  const orderBuild = async () => {
    setApplying(true);
    try {
      const ids = Object.keys(approved).filter((id) => approved[id]);
      await api.post(`/voice/session/${review.session_id}/apply`, { approved_proposal_ids: ids, edits });
      toast.success("Blueprint updated. Approve your scope, then pick a build.");
      navigate("/build?order=1");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not continue to Build Studio."); }
    finally { setApplying(false); }
  };

  if (loading) return <FullPageLoader label="Preparing your voice interview…" />;

  const progress = session?.progress;
  const requirements = session?.requirements || [];
  const assumptions = session?.assumptions || [];
  const missing = session?.missing_info || [];
  const proposals = session?.proposals || [];
  const timeUp = remaining !== null && remaining <= 0;
  const lowTime = remaining !== null && remaining <= 120;

  const FIELD_LABEL = {
    pages: "Page", features: "Feature", integrations: "Integration",
    acceptance_criteria: "Acceptance criterion", exclusions: "Exclusion",
    product_type: "Product type", product_reason: "Product reason",
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl px-6 py-8 ltifade" data-testid="voice-interview">
        <button onClick={() => navigate("/build")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand" data-testid="voice-back-btn">
          <ArrowLeft size={15} /> Back to Build Studio
        </button>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-muted">
          <Radio size={14} /> Blueprint Voice Interview
        </div>
        <h1 className="mt-1.5 font-heading text-2xl font-semibold tracking-tight">Answer a few questions and we'll refine your blueprint</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          A short spoken interview, in English, up to {15} minutes. We listen, note what your product needs, and
          suggest changes. Nothing gets saved to your blueprint until you approve it at the end.
        </p>

        {phase === "intro" ? (
          <div className="mt-8 rounded-2xl border border-brand-line bg-white p-10 text-center shadow-soft" data-testid="voice-intro">
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-brand-surface"><Mic size={30} className="text-brand" /></span>
            <p className="mt-4 font-heading text-lg font-medium">Ready when you are</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
              We'll ask one question at a time. Speak naturally. You can switch to typing, repeat a question,
              skip a topic, or end early anytime.
            </p>
            <button data-testid="voice-start-btn" onClick={start}
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover">
              <Mic size={16} /> Start voice interview
            </button>
          </div>
        ) : phase === "active" ? (
          <div className="mt-6 grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)_300px]">
            {/* LEFT — progress */}
            <aside className="rounded-2xl border border-brand-line bg-white p-5 shadow-soft" data-testid="voice-progress">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Interview progress</p>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-brand-surface">
                <div className="h-full rounded-full bg-brand transition-all duration-500"
                  style={{ width: `${Math.round(((progress?.topic_index || 0) / (progress?.total || 1)) * 100)}%` }} />
              </div>
              <ul className="mt-4 space-y-2.5">
                {(progress?.topics || []).map((t, i) => {
                  const current = i === progress.topic_index && !progress.done;
                  return (
                    <li key={t.key} className="flex items-center gap-2.5 text-sm" data-testid={`topic-${t.key}`}>
                      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-semibold ${t.done ? "bg-emerald-500 text-white" : current ? "bg-brand text-white" : "bg-brand-surface text-slate-400"}`}>
                        {t.done ? <Check size={11} /> : i + 1}
                      </span>
                      <span className={current ? "font-medium text-brand-text" : t.done ? "text-slate-500" : "text-slate-400"}>{t.label}</span>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* CENTER — voice + transcript + controls */}
            <section className="rounded-2xl border border-brand-line bg-white p-6 shadow-soft">
              {remaining !== null && (
                <div className={`mb-4 flex items-center justify-between rounded-lg px-3 py-2 text-xs font-medium ${timeUp ? "bg-red-50 text-red-600" : lowTime ? "bg-amber-50 text-amber-700" : "bg-brand-surface/50 text-brand-muted"}`} data-testid="voice-timer">
                  <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {timeUp ? "Time's up" : "Time remaining"}</span>
                  <span className="font-semibold tabular-nums">{fmtTime(remaining)}</span>
                </div>
              )}
              {timeUp && (
                <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600" data-testid="voice-timeup-note">
                  You've reached the 15 minute limit. Please end and review what we captured.
                </p>
              )}
              <div className="flex items-center gap-3 rounded-xl bg-brand-surface/40 p-4">
                <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-full ${speaking ? "animate-pulse bg-brand text-white" : "bg-white text-brand"}`}>
                  <Volume2 size={20} />
                </span>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-muted">{speaking ? "Speaking" : "Question"}</p>
                  <p className="mt-0.5 text-[15px] font-medium leading-snug text-brand-text" data-testid="voice-question">{question?.text}</p>
                </div>
              </div>

              {lastTranscript && (
                <div className="mt-4" data-testid="voice-transcript">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-muted">You said</p>
                  <p className="mt-1 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">{lastTranscript}</p>
                </div>
              )}

              {/* Mic / recording */}
              <div className="mt-6 flex flex-col items-center">
                {busy ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="voice-processing">
                    <Loader2 size={16} className="animate-spin" /> Thinking about your answer…
                  </div>
                ) : textMode ? (
                  <div className="w-full" data-testid="voice-text-panel">
                    <textarea data-testid="voice-text-input" value={textValue} onChange={(e) => setTextValue(e.target.value)} rows={3}
                      placeholder="Type your answer…" className="w-full rounded-xl border border-brand-line px-3 py-2 text-sm" />
                    <div className="mt-2 flex justify-center gap-2">
                      <button data-testid="voice-text-send-btn" disabled={!textValue.trim() || timeUp}
                        onClick={() => sendAnswer({ text: textValue.trim() })}
                        className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50">
                        <Check size={15} /> Send answer
                      </button>
                      <button onClick={() => setTextMode(false)} className="inline-flex items-center gap-2 rounded-lg border border-brand-line px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                        <Mic size={15} /> Use voice
                      </button>
                    </div>
                  </div>
                ) : !recording ? (
                  <button data-testid="voice-record-btn" onClick={startRecording} disabled={timeUp}
                    className="grid h-20 w-20 place-items-center rounded-full bg-brand text-white shadow-lg transition-all duration-200 hover:scale-105 hover:bg-brand-hover disabled:opacity-40 disabled:hover:scale-100">
                    <Mic size={30} />
                  </button>
                ) : (
                  <div className="flex flex-col items-center">
                    <button data-testid="voice-stop-btn" onClick={stopRecording}
                      className="grid h-20 w-20 place-items-center rounded-full bg-red-500 text-white shadow-lg ring-4 ring-red-100 transition-all duration-200 hover:scale-105">
                      <Square size={26} />
                    </button>
                    <div className="mt-3 flex items-center gap-2 text-xs font-medium text-red-500">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> {paused ? "Paused" : "Recording…"}
                    </div>
                  </div>
                )}
                {!busy && !textMode && (
                  <p className="mt-3 text-xs text-slate-400">{recording ? "Tap the square to stop and send" : "Tap the mic and speak your answer"}</p>
                )}
              </div>

              {/* Control bar */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2 border-t border-brand-line pt-4">
                {recording && !paused && (
                  <button data-testid="voice-pause-btn" onClick={pauseRecording} className="ctrl"><Pause size={14} /> Pause</button>
                )}
                {recording && paused && (
                  <button data-testid="voice-resume-btn" onClick={resumeRecording} className="ctrl"><Play size={14} /> Resume</button>
                )}
                <button data-testid="voice-mute-btn" onClick={toggleMute} className="ctrl">
                  {muted ? <VolumeX size={14} /> : <Volume2 size={14} />} {muted ? "Unmute" : "Mute"}
                </button>
                <button data-testid="voice-repeat-btn" onClick={repeat} disabled={busy} className="ctrl"><RefreshCw size={14} /> Repeat</button>
                <button data-testid="voice-skip-btn" onClick={skip} disabled={busy || recording} className="ctrl"><SkipForward size={14} /> Skip</button>
                <button data-testid="voice-text-toggle-btn" onClick={() => { if (recording) stopRecording(); setTextMode((v) => !v); }} className="ctrl">
                  <Keyboard size={14} /> {textMode ? "Voice" : "Type"}
                </button>
                <button data-testid="voice-end-btn" onClick={() => endInterview(false)} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-deep px-3.5 py-2 text-xs font-medium text-white transition-all hover:opacity-90 disabled:opacity-50">
                  <PhoneOff size={14} /> End & review
                </button>
              </div>
            </section>

            {/* RIGHT — extraction */}
            <aside className="rounded-2xl border border-brand-line bg-white p-5 shadow-soft" data-testid="voice-requirements">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-muted"><ListChecks size={13} /> Detected requirements</p>
              {requirements.length ? (
                <ul className="mt-2.5 space-y-1.5">
                  {requirements.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check size={13} className="mt-0.5 shrink-0 text-emerald-500" /> {r.text}</li>
                  ))}
                </ul>
              ) : <p className="mt-2 text-sm text-slate-400">We'll capture these as you talk.</p>}

              {assumptions.length > 0 && (
                <>
                  <p className="mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-muted"><Lightbulb size={13} /> Assumptions</p>
                  <ul className="mt-2 space-y-1.5">
                    {assumptions.map((a, i) => <li key={i} className="text-sm text-slate-500">• {a}</li>)}
                  </ul>
                </>
              )}
              {missing.length > 0 && (
                <>
                  <p className="mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-muted"><HelpCircle size={13} /> Still missing</p>
                  <ul className="mt-2 space-y-1.5">
                    {missing.map((m, i) => <li key={i} className="text-sm text-amber-600">• {m}</li>)}
                  </ul>
                </>
              )}

              <p className="mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-muted" data-testid="voice-proposals"><Wand2 size={13} /> Proposed updates</p>
              {proposals.length ? (
                <ul className="mt-2 space-y-2">
                  {proposals.map((p) => (
                    <li key={p.id} className="rounded-lg border border-brand-line bg-brand-surface/30 p-2.5 text-sm">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-brand">{FIELD_LABEL[p.field] || p.field}</span>
                      <p className="text-slate-600">{p.value}</p>
                    </li>
                  ))}
                </ul>
              ) : <p className="mt-2 text-sm text-slate-400">Suggestions will appear here. You approve them at the end.</p>}
            </aside>
          </div>
        ) : (
          /* REVIEW */
          <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]" data-testid="voice-review">
            <section className="rounded-2xl border border-brand-line bg-white p-6 shadow-soft">
              <p className="flex items-center gap-2 font-heading text-lg font-medium"><ShieldCheck size={18} className="text-brand" /> Review before anything changes</p>
              {review?.summary && <p className="mt-2 rounded-xl bg-brand-surface/40 p-4 text-sm leading-relaxed text-slate-600" data-testid="review-summary">{review.summary}</p>}

              <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-brand-muted">Proposed blueprint updates</p>
              {(review?.proposals || []).length ? (
                <ul className="mt-3 space-y-2.5">
                  {review.proposals.map((p) => (
                    <li key={p.id} className="rounded-xl border border-brand-line p-3" data-testid={`review-proposal-${p.id}`}>
                      <label className="flex items-start gap-2.5">
                        <input type="checkbox" data-testid={`proposal-checkbox-${p.id}`} className="mt-1 h-4 w-4 accent-brand"
                          checked={!!approved[p.id]} onChange={(e) => setApproved((s) => ({ ...s, [p.id]: e.target.checked }))} />
                        <div className="min-w-0 flex-1">
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-brand">{FIELD_LABEL[p.field] || p.field} · {p.action}</span>
                          <input data-testid={`proposal-edit-${p.id}`} value={edits[p.id] ?? p.value}
                            onChange={(e) => setEdits((s) => ({ ...s, [p.id]: e.target.value }))}
                            className="mt-1 w-full rounded-lg border border-brand-line px-2.5 py-1.5 text-sm text-slate-700" />
                          {p.rationale && <p className="mt-1 text-xs text-slate-400">{p.rationale}</p>}
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : <p className="mt-2 text-sm text-slate-400">No blueprint changes were proposed. You can still continue or head back.</p>}

              <div className="mt-6 flex flex-wrap gap-2">
                <button data-testid="voice-approve-btn" onClick={applyUpdates} disabled={applying}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-white transition-all hover:bg-brand-hover disabled:opacity-50">
                  {applying ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Approve and update blueprint
                </button>
                <button data-testid="voice-order-build-btn" onClick={orderBuild} disabled={applying}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-deep px-4 py-2.5 text-sm font-medium text-white transition-all hover:opacity-90 disabled:opacity-50">
                  <ShoppingCart size={15} /> Order this build
                </button>
                <button data-testid="voice-continue-btn" onClick={continueInterview}
                  className="inline-flex items-center gap-2 rounded-lg border border-brand-line px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                  <Mic size={15} /> Continue with more questions
                </button>
                <button data-testid="voice-download-recap-btn" onClick={downloadRecap}
                  className="inline-flex items-center gap-2 rounded-lg border border-brand-line px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                  <Download size={15} /> Download recap (PDF)
                </button>
                <button onClick={() => navigate("/build")} className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-brand">
                  Discard and go back
                </button>
              </div>
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck size={12} /> Only the boxes you check will be written to your blueprint.</p>
            </section>

            <aside className="space-y-4">
              {(review?.recommendations || []).length > 0 && (
                <div className="rounded-2xl border border-brand-line bg-white p-5 shadow-soft">
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-muted"><Lightbulb size={13} /> Recommendations</p>
                  <ul className="mt-2.5 space-y-1.5">
                    {review.recommendations.map((r, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><ArrowRightDot /> {r}</li>)}
                  </ul>
                </div>
              )}
              {(review?.final_risks || []).length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5">
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700"><AlertTriangle size={13} /> Risks to watch</p>
                  <ul className="mt-2.5 space-y-1.5">
                    {review.final_risks.map((r, i) => <li key={i} className="text-sm text-amber-700">• {r}</li>)}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function ArrowRightDot() {
  return <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />;
}
